import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  buildWorkerApprovalUrl,
  cancelApproval,
  fetchApproval,
  type DocumentApproval,
  sendApprovalReminder,
} from '../services/documentApprovalApi';
import { getCentralDbUrls } from '../services/centralDbClient';

const statusColor = (s: string) => {
  switch (s) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getWorkerName(doc: DocumentApproval): string {
  const w = doc.worker;
  if (!w) return doc.workerId;
  return [w.firstName, w.prefix, w.lastName].filter(Boolean).join(' ');
}

function getDisplayedRecipientEmail(doc: DocumentApproval): string | null {
  return doc.workerEmail || doc.worker?.email || null;
}

const DocumentApprovalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);

  const [approval, setApproval] = useState<DocumentApproval | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [reminding, setReminding] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadApproval = useCallback(async () => {
    if (!accessToken || !id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchApproval(accessToken, id);
      setApproval(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load document approval');
    } finally {
      setLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadApproval();
  }, [loadApproval]);

  const handleSendReminder = async () => {
    if (!approval) return;
    setReminding(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await sendApprovalReminder(accessToken, approval.id);
      setApproval(updated);
      setSuccess('Reminder email sent.');
    } catch (err: any) {
      setError(err?.message || 'Failed to send reminder');
    } finally {
      setReminding(false);
    }
  };

  const handleCancel = async () => {
    if (!approval) return;
    setCancelling(true);
    setError(null);
    setSuccess(null);
    try {
      await cancelApproval(accessToken, approval.id);
      navigate('/documents', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Failed to cancel approval');
    } finally {
      setCancelling(false);
    }
  };

  const approvalUrl = approval ? buildWorkerApprovalUrl(approval) : null;
  const fileUrl = approval?.fileUrl
    ? approval.fileUrl.startsWith('http')
      ? approval.fileUrl
      : `${getCentralDbUrls().origin}${approval.fileUrl}`
    : null;

  const reminderEvents = useMemo(
    () => [...(approval?.reminderEvents ?? [])].sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt)),
    [approval?.reminderEvents],
  );
  const openEvents = useMemo(
    () => [...(approval?.openEvents ?? [])].sort((a, b) => Date.parse(b.openedAt) - Date.parse(a.openedAt)),
    [approval?.openEvents],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!approval) {
    return (
      <Stack spacing={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/documents')} sx={{ alignSelf: 'flex-start' }}>
          Back to document signing
        </Button>
        <Alert severity="error">{error || 'Document approval not found.'}</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <div>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/documents')} sx={{ mb: 1, ml: -1 }}>
            Back to document signing
          </Button>
          <Typography variant="h5" fontWeight={800}>
            {approval.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {approval.id}
          </Typography>
        </div>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip label={approval.status} color={statusColor(approval.status) as any} />
          {approval.status === 'pending' && (
            <Button
              variant="contained"
              startIcon={reminding ? <CircularProgress size={16} /> : <MailOutlineIcon />}
              onClick={handleSendReminder}
              disabled={reminding}
            >
              {reminding ? 'Sending...' : 'Send Reminder'}
            </Button>
          )}
          {approval.status === 'pending' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Approval'}
            </Button>
          )}
        </Stack>
      </Stack>

      {success && <Alert severity="success">{success}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} flexWrap="wrap">
            <div>
              <Typography variant="caption" color="text.secondary">Worker</Typography>
              <Typography variant="body2">{getWorkerName(approval)}</Typography>
              {getDisplayedRecipientEmail(approval) && (
                <Typography variant="caption" color="text.secondary">{getDisplayedRecipientEmail(approval)}</Typography>
              )}
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Document Type</Typography>
              <Typography variant="body2">{approval.documentType.replace(/_/g, ' ')}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography variant="body2">{formatDate(approval.createdAt)}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Expires</Typography>
              <Typography variant="body2">{formatDate(approval.expiresAt)}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Reminders</Typography>
              <Typography variant="body2">{approval.reminderCount ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Last: {formatDate(approval.lastReminderAt ?? null)}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="text.secondary">Opened</Typography>
              <Typography variant="body2">{approval.openedCount ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">Last: {formatDate(approval.lastOpenedAt ?? null)}</Typography>
            </div>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            {approvalUrl ? (
              <div>
                <Typography variant="caption" color="text.secondary">Direct signing link</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <TextField fullWidth size="small" value={approvalUrl} slotProps={{ input: { readOnly: true } }} />
                  <IconButton size="small" onClick={() => navigator.clipboard.writeText(approvalUrl)} title="Copy to clipboard">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Button variant="outlined" href={approvalUrl} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />} sx={{ mt: 1 }}>
                  Open signing page
                </Button>
              </div>
            ) : approval.status === 'pending' ? (
              <Alert severity="warning">
                No magic link was generated for this approval. The worker has not received an email and cannot access this document.
              </Alert>
            ) : null}

            {approval.description && (
              <div>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{approval.description}</Typography>
              </div>
            )}

            {approval.emailMessage && (
              <div>
                <Typography variant="caption" color="text.secondary">Email message</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{approval.emailMessage}</Typography>
              </div>
            )}

            {approval.signingMessage && (
              <div>
                <Typography variant="caption" color="text.secondary">Signing message</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{approval.signingMessage}</Typography>
              </div>
            )}
          </Stack>
        </CardContent>
      </Card>

      {(reminderEvents.length > 0 || openEvents.length > 0) && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              {reminderEvents.length > 0 && (
                <div>
                  <Typography variant="subtitle2" gutterBottom>Reminder history</Typography>
                  <Stack spacing={0.75}>
                    {reminderEvents.map((event, index) => (
                      <Typography key={`${event.sentAt}-${index}`} variant="body2">
                        {formatDate(event.sentAt)} · {event.channel} · {event.deliveryMode} · {event.recipient}
                      </Typography>
                    ))}
                  </Stack>
                </div>
              )}
              {openEvents.length > 0 && (
                <div>
                  <Typography variant="subtitle2" gutterBottom>Signing page opens</Typography>
                  <Stack spacing={0.75}>
                    {openEvents.map((event, index) => (
                      <Typography key={`${event.openedAt}-${index}`} variant="body2">
                        {formatDate(event.openedAt)} · {event.accessMode === 'public_link' ? 'public link' : 'worker session'}
                      </Typography>
                    ))}
                  </Stack>
                </div>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {(approval.status === 'approved' || approval.status === 'rejected' || approval.signatureData || approval.htmlContent || fileUrl) && (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              {approval.status === 'approved' && (
                <div>
                  <Typography variant="caption" color="text.secondary">Approved at</Typography>
                  <Typography variant="body2">{formatDate(approval.approvedAt)}</Typography>
                </div>
              )}

              {approval.status === 'rejected' && (
                <div>
                  <Typography variant="caption" color="text.secondary">Rejected at</Typography>
                  <Typography variant="body2">{formatDate(approval.rejectedAt)}</Typography>
                  {approval.rejectionReason && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Reason</Typography>
                      <Typography variant="body2">{approval.rejectionReason}</Typography>
                    </>
                  )}
                </div>
              )}

              {approval.signatureData && (
                <div>
                  <Typography variant="subtitle2" gutterBottom>Signature</Typography>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: '#fff', maxWidth: 400 }}>
                    <img src={approval.signatureData} alt="Worker signature" style={{ maxWidth: '100%', maxHeight: 150 }} />
                  </Box>
                </div>
              )}

              <Divider />

              <div>
                <Typography variant="subtitle2" gutterBottom>Document content</Typography>
                {approval.htmlContent && (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 500, overflow: 'auto', bgcolor: '#fff', mb: 2 }}>
                    <iframe srcDoc={approval.htmlContent} title="Document content" sandbox="" style={{ width: '100%', height: 500, border: 'none' }} />
                  </Box>
                )}
                {fileUrl && (
                  <Button variant="outlined" size="small" href={fileUrl} target="_blank" rel="noopener noreferrer">
                    Open PDF
                  </Button>
                )}
                {!approval.htmlContent && !fileUrl && (
                  <Typography variant="body2" color="text.secondary">No document content available.</Typography>
                )}
              </div>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default DocumentApprovalDetailPage;
